import produce from 'immer';
import React, {useState} from 'react';
import {NodeProps, useReactFlow} from 'reactflow';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CustomMappingContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/CustomMappingContext';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  convertRootRowToMapFromStartRow,
  filterToAddableMapFromStartRows,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/mapFromStartUtils';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNodeSectionType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNodeData,
  TaskSchemaNodeRowData,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  isLookupConfiguredForEdge,
  FlowchartNodeChangeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';

import {NodesContext} from '../NodesContext';
import AddFieldToSectionButton from './AddFieldToSectionButton';
import createHeaderPropsForNode from './createHeaderPropsForNode';
import {getNodeAndRowStatusAndDescription} from './nodeUtils';
import {TaskSchemaNodeRowRenderer} from './TaskSchemaNodeRowRenderer';

export interface TaskSchemaNodeRendererProps extends NodeProps<TaskSchemaNodeData> {}

export default function TaskSchemaNodeRenderer(props: TaskSchemaNodeRendererProps): JSX.Element {
  const {data, xPos, yPos, id} = props;
  // Row and handle hovers do no propagate up. This hovered state primarily indicates hovering on the header
  const [isHovered, setIsHovered] = useState(false);
  const reactFlowInstance = useReactFlow();
  const {sections, onNodeHeaderClick, onUpdateNodeData} = data;
  const locked = ReactFlowLockedContext.useContext() ?? false;

  const headerProps = createHeaderPropsForNode(
    {
      ...data,
      position: {x: xPos, y: yPos},
    },
    reactFlowInstance,
    data.nodeDetailsType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
    data.nodeDetailsType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    locked
  );

  const handleHeaderClick = (): void => {
    onNodeHeaderClick?.(id);
  };
  const nodesContext = NodesContext.useContext();
  const customMappingDataByEdgeId = CustomMappingContext.useContext();

  // Check if lookup is configured for a specific row
  const isLookupConfiguredForRow = (nodeId: string, rowClientId: string): boolean => {
    const edges = reactFlowInstance.getEdges();

    const relevantEdges = edges.filter(
      (edge) =>
        edge.source === nodeId &&
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.sourceHandle &&
        getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId === rowClientId
    );

    return relevantEdges.some((edge) => isLookupConfiguredForEdge(edge.id, customMappingDataByEdgeId));
  };

  const {nodeStatus, nodeDescription, rowStatusAndDescriptionById} = getNodeAndRowStatusAndDescription(
    data.validationErrors
  );

  const mapFromStartSection = data.sections.find(
    (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
  );
  let addFieldToMapFromStartSectionButton: JSX.Element | null;
  if (mapFromStartSection) {
    const rootRows = nodesContext.startNode?.data.sections.flatMap((section) => section.sectionRows) ?? [];
    const addableRows = filterToAddableMapFromStartRows(rootRows, mapFromStartSection.sectionRows);
    addFieldToMapFromStartSectionButton = (
      <AddFieldToSectionButton
        rows={addableRows}
        icon="plus"
        disabled={locked || addableRows.length === 0}
        onAddFieldToSection={(rowData: TaskSchemaNodeRowData) => {
          onUpdateNodeData(
            (flowchartConfig: FlowchartConfigWithMetadata) => {
              const newNodeData = produce(data, (draftNode) => {
                draftNode.sections = draftNode.sections.map((section) => {
                  if (section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
                    section.sectionRows.push(convertRootRowToMapFromStartRow(rowData));
                    return section;
                  }
                  return section;
                });
              });
              return [{id: data.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE}];
            },
            false,
            true // Skip setting dirty state to prevent autosave when adding map from start field
          );
        }}
      />
    );
  } else {
    addFieldToMapFromStartSectionButton = null;
  }

  return (
    <BenchlingNodeV2
      isSelected={props.selected}
      status={nodeStatus}
      statusDescription={nodeDescription}
      onMouseOver={() => {
        setIsHovered(true);
      }}
      onMouseOut={() => {
        setIsHovered(false);
      }}
      dataTestAttrs={{
        element: 'TaskSchemaNodeRenderer',
        key: headerProps.title,
      }}
    >
      {headerProps && (
        <BenchlingNodeV2.Standard.Header
          {...headerProps}
          onClick={handleHeaderClick}
          style={{
            cursor: onNodeHeaderClick != null ? 'pointer' : 'default',
          }}
        />
      )}
      {sections && (
        <BenchlingNodeV2.Sections>
          {sections.map((section: TaskSchemaNodeSectionData) => (
            <BenchlingNodeV2.Section
              key={section.sectionName}
              leftHeader={section.sectionName}
              rightHeader={
                section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
                  ? addFieldToMapFromStartSectionButton
                  : undefined
              }
            >
              {section.sectionRows.map((row) => (
                <TaskSchemaNodeRowRenderer
                  key={row.id}
                  {...row}
                  status={rowStatusAndDescriptionById[row.clientId]?.status ?? undefined}
                  statusDescription={rowStatusAndDescriptionById[row.clientId]?.description ?? undefined}
                  secondaryIcon={isLookupConfiguredForRow(id, row.clientId) ? 'entity-lookup' : undefined}
                  isHovered={isHovered}
                />
              ))}
            </BenchlingNodeV2.Section>
          ))}
        </BenchlingNodeV2.Sections>
      )}
    </BenchlingNodeV2>
  );
}
